import {useCallback, useEffect, useState} from "react";
import {useNavigate, useParams} from "react-router-dom";
import {useAuth} from "../../../providers/AuthenticationProvider";
import {Form, Message} from "semantic-ui-react";
import {useWeatherStation} from "../../../providers/WeatherStationProvider";
import {ConfirmationModal} from "../modals/ConfirmationModal";

const WEB_PAGE = 'Weather Stations';

export const WeatherStationEdit = () => {
    const navigate = useNavigate();
    const { checkAuth, getUserType } = useAuth();
    const { getWeatherStationById, editWeatherStation, deleteWeatherStation } = useWeatherStation();
    const { id } = useParams();
    const [name, setName] = useState('');
    const [usaf, setUsaf] = useState('');
    const [wban, setWban] = useState('');
    const [icao, setIcao] = useState('');
    const [loading, setLoading] = useState(false);
    const [errors, setErrors] = useState([]);
    const [confirmationOpen, setConfirmationOpen] = useState(false);
    
    useEffect(() => {
        checkAuth(getUserType(), WEB_PAGE) && navigate('/');
    }, [getUserType, checkAuth, navigate]);
    
    const refreshWeatherStation = useCallback(async () => {
        const data = await getWeatherStationById(id);
        setName(data.name);
        setUsaf(data.usaf);
        setWban(data.wban);
        setIcao(data.icao);
    }, [getWeatherStationById, setName, setUsaf, setWban, setIcao]);

    useEffect(() => {
        refreshWeatherStation();
    }, [id, refreshWeatherStation]);
    
    const handleSubmit = e => {
        e.preventDefault();
        if (!name.trim() || !usaf.trim() || !wban.trim() || !icao.trim()) {
            setErrors(['All fields are required and must not be empty.'])
            return;
        }
        
        setLoading(true);
        editWeatherStation(id, name, usaf, wban, icao)
            .then(res => {
                setLoading(true);
                navigate('/weather-stations');
            })
            .catch(err => {
                if (err?.response?.data?.errors) {
                    setErrors(
                        err.response.data.errors.map(error => {
                            return error.description;
                        })
                    );
                } else {
                    setErrors(['Failed to update weather station.']);
                }
                setLoading(false);
            });
    };
    
    const handleDelete = e => {
        e.preventDefault();
        setConfirmationOpen(true);
    }
    
    const handleConfirmDelete = e => {
        deleteWeatherStation(id)
            .then(res => {
                setLoading(true);
                navigate('/weather-stations');
            })
            .catch(err => {
                if (err?.response?.data?.errors) {
                    setErrors(
                        err.response.data.errors.map(error => {
                            return error.description;
                        })
                    );
                } else {
                    setErrors(['Failed to delete weather station.']);
                }
                setLoading(false);
                setConfirmationOpen(false);
        })
    };
     
    return (
    <>
        <ConfirmationModal 
            open={confirmationOpen}
            setOpen={setConfirmationOpen}
            submitAction={handleConfirmDelete}
        />
        <div>
            <div className="pageHeader">
              <h1>Edit a Weather Station</h1>
            </div>
            <Form onSubmit={handleSubmit} error={errors.length > 0} style={{ maxWidth: '600px' }}>
              <Form.Group>
                  <Form.Input
                    width={16}
                    label="Name"
                    defaultValue={name}
                    onChange={e => {
                        setName(e.target.value);
                    }}
                    type="text"
                    required
                  />
              </Form.Group>
              <Form.Group widths={3}>
                  <Form.Input
                    label="USAF"
                    defaultValue={usaf}
                    onChange={e => {
                        setUsaf(e.target.value);
                    }}
                    type="text"
                    required
                  />
                  <Form.Input
                    label="WBAN"
                    defaultValue={wban}
                    onChange={e => {
                        setWban(e.target.value);
                    }}
                    type="text"
                    required
                  />
                  <Form.Input
                    label="ICAO"
                    defaultValue={icao}
                    onChange={e => {
                        setIcao(e.target.value);
                    }}
                    type="text"
                    required
                  />
              </Form.Group>
              {errors.length > 0 ? (
                  <Form.Group>
                      <Message className="errorList" error list={errors} />
                  </Form.Group>
              ) : null}
              <Form.Group>
                  <Form.Button
                    type="submit"
                    size="large"
                    loading={loading}
                    color="blue"
                    style={{ marginTop: '32px' }}
                  >
                      Update
                  </Form.Button>
                  <Form.Button
                    size="large"
                    style={{ marginTop: '32px' }}
                    onClick={e => {
                        e.preventDefault();
                        navigate('/weather-stations')
                    }}
                  >
                      Cancel
                  </Form.Button>
                  <Form.Button
                    size="large"
                    style={{ marginTop: '32px' }}
                    onClick={handleDelete}
                    color="red"
                  >
                      Delete
                  </Form.Button>
              </Form.Group>
            </Form>
        </div> 
    </>      
    );
}
